import { useMemo, useState } from 'react'
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Copy,
  Package,
  Plus,
  Save,
  Trash2,
} from 'lucide-react'
import {
  COMMERCIAL_INVOICE_LINE_ITEM_FIELDS,
  COMMERCIAL_INVOICE_SECTIONS,
  INVOICE_DOCUMENT_SOURCE_OPTIONS,
  INVOICE_RECONCILIATION_STATUS_OPTIONS,
  INVOICE_RISK_OPTIONS,
  INVOICE_TYPE_OPTIONS,
} from '@/features/case-workspace/invoiceTemplate'
import { DOCUMENT_DEFINITIONS } from '@/features/case-workspace/schemas'
import type {
  DocumentFieldDefinition,
  DocumentWorkspaceFocusTarget,
  StructuredDocumentRecord,
} from '@/features/case-workspace/types'
import { formatDateTime } from '@/lib/cases'
import {
  FieldControl,
  InlineHelp,
  OptionalSectionNote,
  SectionHeading,
  StatusPill,
  toneForRecommendationState,
} from '@/features/case-workspace/WorkspaceShared'
import { summarizeValidationIssues } from '@/features/case-workspace/mockData'
import { canMarkDocumentComplete, canMarkSectionComplete, isFieldVisible, summarizeSectionStatus } from '@/features/case-workspace/utils'

type CommercialInvoiceEditorProps = {
  record: StructuredDocumentRecord
  focusTarget: DocumentWorkspaceFocusTarget | null
  busy: boolean
  saveMessage: string | null
  onFieldChange: (recordId: string, fieldKey: string, nextValue: string) => void
  onFieldNoteChange: (recordId: string, fieldKey: string, note: string) => void
  onConfirmField: (recordId: string, fieldKey: string) => void
  onSectionNoteChange: (recordId: string, sectionId: string, note: string) => void
  onMarkSectionComplete: (recordId: string, sectionId: string) => void
  onLineItemFieldChange: (recordId: string, lineItemId: string, fieldKey: string, nextValue: string) => void
  onLineItemNoteChange: (recordId: string, lineItemId: string, note: string) => void
  onConfirmLineItemField: (recordId: string, lineItemId: string, fieldKey: string) => void
  onAddLineItem: (recordId: string) => void
  onDuplicateLineItem: (recordId: string, lineItemId: string) => void
  onRemoveLineItem: (recordId: string, lineItemId: string) => void
  onMoveLineItem: (recordId: string, lineItemId: string, direction: 'up' | 'down') => void
  onApplyRecommendation: (recordId: string, recommendationId: string) => void
  onSave: (recordId: string) => Promise<void>
  onAutoSave: (recordId: string, options?: { auto?: boolean }) => Promise<void>
  onMarkComplete: (recordId: string) => void
}

const INVOICE_LINE_GROUPS = [
  {
    id: 'identification',
    title: 'Product Identification',
    fields: ['line_number', 'part_number', 'detailed_merchandise_description', 'grade_quality', 'hts_tariff_number', 'manufacturer_supplier_reference', 'serial_lot_batch_number'],
  },
  {
    id: 'quantity_packaging',
    title: 'Quantity / Packaging',
    fields: ['quantity', 'quantity_uom', 'packaging_type', 'packaging_count', 'marks_numbers_symbols', 'package_marks_numbers'],
  },
  {
    id: 'value_origin',
    title: 'Value / Origin',
    fields: ['country_of_origin_line', 'gross_weight', 'net_weight', 'measurement_volume', 'unit_price', 'extended_line_value', 'currency_line', 'assists_components_material_breakdown', 'line_discrepancy_status'],
  },
]

function toneForSectionStatus(status: ReturnType<typeof summarizeSectionStatus>) {
  switch (status) {
    case 'complete':
      return 'success'
    case 'needs_review':
      return 'warning'
    case 'validation_error':
    case 'cross_document_conflict':
      return 'error'
    case 'in_progress':
      return 'info'
    default:
      return 'neutral'
  }
}

function labelForSectionStatus(status: ReturnType<typeof summarizeSectionStatus>) {
  switch (status) {
    case 'not_started':
      return 'Not Started'
    case 'in_progress':
      return 'In Progress'
    case 'needs_review':
      return 'Needs Review'
    case 'complete':
      return 'Complete'
    case 'validation_error':
      return 'Validation Error'
    case 'cross_document_conflict':
      return 'Cross-Document Conflict'
  }
}

function labelForFieldReviewStatus(status?: StructuredDocumentRecord['fields'][string]['reviewStatus']) {
  switch (status) {
    case 'extracted':
      return 'Extracted'
    case 'manually_entered':
      return 'Manually entered'
    case 'edited_after_extraction':
      return 'Edited after extraction'
    case 'agent_confirmed':
      return 'Agent confirmed'
    case 'unresolved_issue':
      return 'Unresolved'
    default:
      return null
  }
}

function optionLabel(options: Array<{ value: string; label: string }>, value?: string) {
  return options.find((option) => option.value === value)?.label ?? 'Needs review'
}

function parseNumeric(value?: string) {
  const numeric = Number(value ?? '')
  return Number.isFinite(numeric) ? numeric : 0
}

function countPopulatedFields(record: StructuredDocumentRecord, fields: DocumentFieldDefinition[]) {
  const visibleFields = fields.filter((field) => isFieldVisible(field, record.fields))
  const populatedCount = visibleFields.filter((field) => record.fields[field.key]?.value?.trim()).length
  return { populatedCount, totalCount: visibleFields.length }
}

function calculateInvoiceTotals(record: StructuredDocumentRecord) {
  const lines = record.lineItems ?? []
  const calculatedLineValue = lines.reduce(
    (total, line) => total + parseNumeric(line.fields.extended_line_value?.value),
    0,
  )
  const itemizedCharges =
    parseNumeric(record.fields.freight_charge?.value) +
    parseNumeric(record.fields.insurance_charge?.value) +
    parseNumeric(record.fields.commission_charge?.value) +
    parseNumeric(record.fields.cases_charge?.value) +
    parseNumeric(record.fields.containers_charge?.value) +
    parseNumeric(record.fields.coverings_charge?.value) +
    parseNumeric(record.fields.packing_cost?.value) +
    parseNumeric(record.fields.inland_freight_charge?.value) +
    parseNumeric(record.fields.international_freight_charge?.value) +
    parseNumeric(record.fields.royalties_license_fees?.value)

  return {
    calculatedLineValue,
    itemizedCharges,
    declaredInvoiceValue: parseNumeric(record.fields.total_invoice_value?.value),
    declaredTotalCharges: parseNumeric(record.fields.total_charges?.value),
  }
}

export function CommercialInvoiceEditor({
  record,
  focusTarget,
  busy,
  saveMessage,
  onFieldChange,
  onFieldNoteChange,
  onConfirmField,
  onSectionNoteChange,
  onMarkSectionComplete,
  onLineItemFieldChange,
  onLineItemNoteChange,
  onConfirmLineItemField,
  onAddLineItem,
  onDuplicateLineItem,
  onRemoveLineItem,
  onMoveLineItem,
  onApplyRecommendation,
  onSave,
  onAutoSave,
  onMarkComplete,
}: CommercialInvoiceEditorProps) {
  const definition = DOCUMENT_DEFINITIONS.commercial_invoice
  const lineItems = record.lineItems ?? []
  const sectionStates = record.sectionStates ?? {}
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({})
  const [expandedLineIds, setExpandedLineIds] = useState<string[]>(
    lineItems.slice(0, 1).map((line) => line.id),
  )
  const issueMap = record.validationIssues.reduce<Record<string, string[]>>((acc, issue) => {
    acc[issue.fieldKey] = [...(acc[issue.fieldKey] ?? []), issue.message]
    return acc
  }, {})
  const recommendationMap = record.awaRecommendations.reduce<Record<string, StructuredDocumentRecord['awaRecommendations']>>((acc, recommendation) => {
    acc[recommendation.fieldKey] = [...(acc[recommendation.fieldKey] ?? []), recommendation]
    return acc
  }, {})
  const validationSummary = summarizeValidationIssues(record.validationIssues)
  const unresolvedRecommendations = record.awaRecommendations.filter((recommendation) => recommendation.state !== 'resolved').length
  const calculatedTotals = useMemo(() => calculateInvoiceTotals(record), [record])
  const documentSourceLabel = optionLabel(INVOICE_DOCUMENT_SOURCE_OPTIONS, record.fields.document_source?.value)
  const invoiceTypeLabel = optionLabel(INVOICE_TYPE_OPTIONS, record.fields.invoice_type?.value)
  const reconciliationLabel = optionLabel(INVOICE_RECONCILIATION_STATUS_OPTIONS, record.fields.reconciliation_status?.value)
  const riskLabel = optionLabel(INVOICE_RISK_OPTIONS, record.fields.risk_severity?.value)
  const canCompleteDocument = canMarkDocumentComplete(record)
  const isDocumentComplete = record.status === 'complete'

  function toggleSection(sectionId: string, canCollapse: boolean) {
    setOpenSections((current) => {
      const definitionSection = COMMERCIAL_INVOICE_SECTIONS.find((section) => section.id === sectionId)
      const isComplete = sectionStates[sectionId]?.status === 'complete'
      const isOpen = current[sectionId] ?? (isComplete ? false : !definitionSection?.collapsedByDefault)
      if (isOpen && !canCollapse) return current
      return { ...current, [sectionId]: !isOpen }
    })
  }

  function toggleLineItem(lineItemId: string) {
    setExpandedLineIds((current) =>
      current.includes(lineItemId)
        ? current.filter((id) => id !== lineItemId)
        : [...current, lineItemId],
    )
  }

  return (
    <div className="space-y-3">
      <section className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0">
            <SectionHeading
              eyebrow="Commercial Invoice Review Template"
              title={record.title}
              helpText="Use the normalized invoice workspace to review customs-relevant invoice content under 19 CFR Part 141 without depending on a supplier-specific layout."
            />
            <div className="mt-3 flex flex-wrap gap-2">
              <StatusPill label={documentSourceLabel} tone="info" />
              <StatusPill label={invoiceTypeLabel} tone="neutral" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => void onSave(record.id)}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded border border-secondary px-4 py-2 text-sm font-semibold text-secondary hover:bg-secondary-lightest disabled:opacity-60"
            >
              <Save size={16} aria-hidden="true" />
              {busy ? 'Saving…' : 'Save progress'}
            </button>
            <button
              type="button"
              onClick={() => onMarkComplete(record.id)}
              disabled={!isDocumentComplete && !canCompleteDocument}
              className={`inline-flex items-center gap-2 rounded px-4 py-2 text-sm font-semibold ${
                isDocumentComplete
                  ? 'bg-green-600 text-white'
                  : 'bg-primary text-white hover:bg-primary-dark disabled:cursor-not-allowed disabled:bg-neutral-300 disabled:text-neutral-600'
              }`}
            >
              <CheckCircle2 size={16} aria-hidden="true" />
              {isDocumentComplete ? 'Complete' : 'Mark complete'}
            </button>
          </div>
        </div>

        <div className="mt-3 grid gap-2 xl:grid-cols-4">
          <SummaryValue label="Merchandise lines" value={`${lineItems.length}`} />
          <SummaryValue label="Blocking issues" value={`${validationSummary.errors}`} />
          <SummaryValue label="Open AWA items" value={`${unresolvedRecommendations}`} />
          <SummaryValue label="Last updated" value={formatDateTime(record.lastUpdatedAt)} />
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-3">
          <InfoCard
            label="Invoice source"
            value={documentSourceLabel}
            description={
              record.fields.document_source?.value === 'ace_electronic_source'
                ? 'System-supplied invoice data is present. Confirm the extracted values against the case record before acceptance.'
                : record.fields.document_source?.value === 'uploaded_scan'
                  ? 'This invoice was uploaded as a scanned document. Review OCR confidence and complete any missing customs content.'
                  : 'Use this field to explain where the invoice content came from and how much review is still needed.'
            }
          />
          <InfoCard
            label="Reconciliation status"
            value={reconciliationLabel}
            description="Track whether the invoice agrees with the manifest, CBP Form 7501, and other supporting records, or still needs manual comparison."
          />
          <InfoCard
            label="Risk severity"
            value={riskLabel}
            description="Use this to show whether invoice issues are informational, valuation-sensitive, or serious enough to block acceptance or transmit."
          />
        </div>

        {saveMessage && <p className="mt-3 text-sm text-success">{saveMessage}</p>}
      </section>

      {definition.sections.map((section) => {
        const sectionStatus = summarizeSectionStatus(record, section.id)
        const canCompleteSection = canMarkSectionComplete(record, section.id)
        const canCollapseSection = sectionStatus === 'complete'
        const isOpen = openSections[section.id] ?? (sectionStatus === 'complete' ? false : !section.collapsedByDefault)

        return (
          <section key={section.id} className="rounded-lg border border-neutral-200 bg-white shadow-sm">
            <button
              type="button"
              onClick={() => toggleSection(section.id, canCollapseSection)}
              className="flex w-full items-start justify-between gap-4 px-5 py-4 text-left"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h4 className="text-base font-bold text-primary">{section.title}</h4>
                  <StatusPill label={labelForSectionStatus(sectionStatus)} tone={toneForSectionStatus(sectionStatus)} />
                  {section.kind !== 'line_items' && <SectionProgressBadge record={record} fields={section.fields} />}
                </div>
              </div>
              {isOpen ? <ChevronDown size={18} aria-hidden="true" /> : <ChevronRight size={18} aria-hidden="true" />}
            </button>

            {isOpen && (
              <div className="space-y-4 border-t border-neutral-200 px-5 py-5">
                {section.description && (
                  <div className="text-sm text-neutral-600">
                    <InlineHelp text={section.description} title={`${section.title} help`} align="left" />
                  </div>
                )}
                {section.kind === 'line_items' ? (
                  <CommercialInvoiceLineItemsSection
                    record={record}
                    focusTarget={focusTarget}
                    onLineItemFieldChange={onLineItemFieldChange}
                    onLineItemNoteChange={onLineItemNoteChange}
                    onConfirmLineItemField={onConfirmLineItemField}
                    onAddLineItem={onAddLineItem}
                    onDuplicateLineItem={onDuplicateLineItem}
                    onRemoveLineItem={onRemoveLineItem}
                    onMoveLineItem={onMoveLineItem}
                    expandedLineIds={expandedLineIds}
                    toggleLineItem={toggleLineItem}
                    issueMap={issueMap}
                    onAutoSave={onAutoSave}
                  />
                ) : (
                  <>
                    <div className="grid gap-4 md:grid-cols-2">
                      {section.fields
                        .filter((field) => isFieldVisible(field, record.fields))
                        .map((field) => (
                          <InvoiceFieldCard
                            key={field.key}
                            record={record}
                            field={field}
                            focusTarget={focusTarget}
                            issues={issueMap[field.key] ?? []}
                            recommendations={recommendationMap[field.key] ?? []}
                            onFieldChange={onFieldChange}
                            onFieldNoteChange={onFieldNoteChange}
                            onConfirmField={onConfirmField}
                            onApplyRecommendation={onApplyRecommendation}
                            onAutoSave={onAutoSave}
                          />
                        ))}
                    </div>
                    {section.id === 'valuation_charges' && (
                      <InvoiceTotalsCard
                        record={record}
                        calculatedTotals={calculatedTotals}
                        onSectionNoteChange={onSectionNoteChange}
                        onAutoSave={onAutoSave}
                      />
                    )}
                  </>
                )}

                <OptionalSectionNote
                  label="Section Review"
                  value={sectionStates[section.id]?.note ?? ''}
                  onChange={(nextValue) => onSectionNoteChange(record.id, section.id, nextValue)}
                  onBlur={() => void onAutoSave(record.id, { auto: true })}
                  placeholder="Add an optional note for this section."
                  actions={
                    <button
                      type="button"
                      onClick={() => {
                        onMarkSectionComplete(record.id, section.id)
                        void onAutoSave(record.id, { auto: true })
                        if (sectionStatus === 'complete') {
                          setOpenSections((current) => ({ ...current, [section.id]: true }))
                        } else {
                          setOpenSections((current) => ({ ...current, [section.id]: false }))
                        }
                      }}
                      disabled={sectionStatus !== 'complete' && !canCompleteSection}
                      className={`rounded px-2.5 py-1 text-xs font-medium ${
                        sectionStatus === 'complete'
                          ? 'bg-green-600 text-white'
                          : 'bg-primary text-white hover:bg-primary-dark disabled:cursor-not-allowed disabled:bg-neutral-300 disabled:text-neutral-600'
                      }`}
                    >
                      {sectionStatus === 'complete' ? 'Complete' : 'Mark as Complete'}
                    </button>
                  }
                />
              </div>
            )}
          </section>
        )
      })}
    </div>
  )
}

function SummaryValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3">
      <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="mt-2 text-sm font-semibold text-primary">{value}</div>
    </div>
  )
}

function InfoCard({ label, value, description }: { label: string; value: string; description: string }) {
  return (
    <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
      <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="mt-2 text-sm font-semibold text-primary">{value}</div>
      <p className="mt-2 text-sm text-neutral-600">{description}</p>
    </div>
  )
}

function SectionProgressBadge({
  record,
  fields,
}: {
  record: StructuredDocumentRecord
  fields: DocumentFieldDefinition[]
}) {
  const { populatedCount, totalCount } = countPopulatedFields(record, fields)
  return <span className="text-xs font-medium text-neutral-500">{`${populatedCount}/${totalCount} fields`}</span>
}

function InvoiceFieldCard({
  record,
  field,
  focusTarget,
  issues,
  recommendations,
  onFieldChange,
  onFieldNoteChange,
  onConfirmField,
  onApplyRecommendation,
  onAutoSave,
}: {
  record: StructuredDocumentRecord
  field: DocumentFieldDefinition
  focusTarget: DocumentWorkspaceFocusTarget | null
  issues: string[]
  recommendations: StructuredDocumentRecord['awaRecommendations']
  onFieldChange: (recordId: string, fieldKey: string, nextValue: string) => void
  onFieldNoteChange: (recordId: string, fieldKey: string, note: string) => void
  onConfirmField: (recordId: string, fieldKey: string) => void
  onApplyRecommendation: (recordId: string, recommendationId: string) => void
  onAutoSave: (recordId: string, options?: { auto?: boolean }) => Promise<void>
}) {
  const fieldState = record.fields[field.key]
  const isFocused = focusTarget?.documentId === record.id && focusTarget.fieldKey === field.key
  const reviewLabel = labelForFieldReviewStatus(fieldState?.reviewStatus)
  const isAgentConfirmed = fieldState?.reviewStatus === 'agent_confirmed'

  return (
    <div className={`rounded-lg border p-4 ${field.type === 'textarea' ? 'md:col-span-2' : ''} ${isFocused ? 'border-accent-sunset bg-accent-sunset/5' : 'border-neutral-200'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-sm font-semibold text-primary">{field.label}</div>
            {field.helpText && <InlineHelp text={field.helpText} title={`${field.label} help`} align="left" />}
          </div>
          {field.officialName && <div className="mt-1 text-xs text-neutral-500">{field.officialName}</div>}
        </div>
        <div className="flex flex-wrap gap-2">
          {reviewLabel && fieldState?.reviewStatus !== 'agent_confirmed' && (
            <StatusPill label={reviewLabel} tone="info" />
          )}
        </div>
      </div>

      <FieldControl
        fieldType={field.type}
        value={fieldState?.value ?? ''}
        options={field.options}
        placeholder={field.placeholder}
        onChange={(value) => onFieldChange(record.id, field.key, value)}
        onBlur={() => void onAutoSave(record.id, { auto: true })}
      />

      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-neutral-500">
        <span>Origin: {(fieldState?.origin ?? 'manual').replace(/_/g, ' ')}</span>
        {fieldState?.sourceValue && <span>Extracted: {fieldState.sourceValue}</span>}
        {fieldState?.sourceConfidence != null && <span>Confidence: {(fieldState.sourceConfidence * 100).toFixed(0)}%</span>}
      </div>

      {fieldState?.conflictNote && (
        <div className="mt-2 rounded border border-warning/30 bg-warning/10 p-2 text-xs text-neutral-700">
          {fieldState.conflictNote}
        </div>
      )}

      {recommendations.length > 0 && (
        <div className="mt-3 space-y-2">
          {recommendations.map((recommendation) => (
            <div key={recommendation.id} className="rounded-lg border border-warning/30 bg-warning/10 p-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-primary">{recommendation.message}</div>
                  <div className="mt-1 text-xs text-neutral-600">
                    Current: {recommendation.currentValue || '—'}
                    {recommendation.recommendedValue ? ` · Recommended: ${recommendation.recommendedValue}` : ''}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusPill label={recommendation.state.replace(/_/g, ' ')} tone={toneForRecommendationState(recommendation.state)} />
                  {recommendation.recommendedValue && (
                    <button
                      type="button"
                      onClick={() => onApplyRecommendation(record.id, recommendation.id)}
                      className="rounded border border-secondary px-2.5 py-1 text-xs font-semibold text-secondary hover:bg-secondary-lightest"
                    >
                      Apply recommendation
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {issues.length > 0 && (
        <ul className="mt-2 space-y-1 text-xs text-error">
          {issues.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      )}

      <div className="mt-3 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => onConfirmField(record.id, field.key)}
          disabled={isAgentConfirmed}
          className={`rounded px-3 py-1.5 text-xs font-semibold ${
            isAgentConfirmed
              ? 'bg-green-100 text-green-800'
              : 'border border-secondary text-secondary hover:bg-secondary-lightest'
          }`}
        >
          {isAgentConfirmed ? 'Agent Confirmed' : 'Mark confirmed'}
        </button>
        {fieldState?.agentConfirmedAt && (
          <span className="text-[11px] text-neutral-500">Confirmed {formatDateTime(fieldState.agentConfirmedAt)}</span>
        )}
      </div>

      <textarea
        rows={2}
        value={fieldState?.reviewNote ?? ''}
        onChange={(event) => onFieldNoteChange(record.id, field.key, event.target.value)}
        onBlur={() => void onAutoSave(record.id, { auto: true })}
        placeholder="Optional field review note"
        className="mt-3 w-full rounded border border-neutral-300 px-3 py-2 text-sm text-neutral-900"
      />
    </div>
  )
}

function CommercialInvoiceLineItemsSection({
  record,
  focusTarget,
  onLineItemFieldChange,
  onLineItemNoteChange,
  onConfirmLineItemField,
  onAddLineItem,
  onDuplicateLineItem,
  onRemoveLineItem,
  onMoveLineItem,
  expandedLineIds,
  toggleLineItem,
  issueMap,
  onAutoSave,
}: {
  record: StructuredDocumentRecord
  focusTarget: DocumentWorkspaceFocusTarget | null
  onLineItemFieldChange: (recordId: string, lineItemId: string, fieldKey: string, nextValue: string) => void
  onLineItemNoteChange: (recordId: string, lineItemId: string, note: string) => void
  onConfirmLineItemField: (recordId: string, lineItemId: string, fieldKey: string) => void
  onAddLineItem: (recordId: string) => void
  onDuplicateLineItem: (recordId: string, lineItemId: string) => void
  onRemoveLineItem: (recordId: string, lineItemId: string) => void
  onMoveLineItem: (recordId: string, lineItemId: string, direction: 'up' | 'down') => void
  expandedLineIds: string[]
  toggleLineItem: (lineItemId: string) => void
  issueMap: Record<string, string[]>
  onAutoSave: (recordId: string, options?: { auto?: boolean }) => Promise<void>
}) {
  const lineItems = record.lineItems ?? []

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-neutral-600">
          <span>Structured merchandise lines</span>
          <InlineHelp text="Review invoice merchandise as structured lines so quantity, package, origin, and value discrepancies can be validated before the case moves forward." title="Merchandise line help" align="left" />
        </div>
        <button
          type="button"
          onClick={() => onAddLineItem(record.id)}
          className="inline-flex items-center gap-2 rounded border border-secondary px-3 py-1.5 text-sm font-semibold text-secondary hover:bg-secondary-lightest"
        >
          <Plus size={14} aria-hidden="true" />
          Add merchandise line
        </button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-neutral-200">
        <div className="sticky top-0 z-10 grid min-w-[70rem] grid-cols-[5rem_14rem_minmax(0,2fr)_9rem_10rem_8rem] gap-3 border-b border-neutral-200 bg-neutral-50 px-4 py-3 text-xs font-semibold uppercase tracking-wider text-neutral-500">
          <div>Line</div>
          <div>Part / SKU</div>
          <div>Description</div>
          <div>Quantity</div>
          <div>Extended Value</div>
          <div>Status</div>
        </div>

        <div className="divide-y divide-neutral-200">
          {lineItems.map((lineItem, index) => {
            const isOpen = expandedLineIds.includes(lineItem.id)
            const rowIssues = Object.entries(issueMap)
              .filter(([fieldKey]) => fieldKey.endsWith(`:${lineItem.id}`))
              .flatMap(([, messages]) => messages)

            return (
              <div key={lineItem.id} className="bg-white">
                <div className="grid min-w-[70rem] grid-cols-[5rem_14rem_minmax(0,2fr)_9rem_10rem_8rem] gap-3 px-4 py-3">
                  <button
                    type="button"
                    onClick={() => toggleLineItem(lineItem.id)}
                    className="inline-flex items-center gap-2 text-left text-sm font-semibold text-primary"
                  >
                    {isOpen ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
                    {lineItem.lineNumber}
                  </button>
                  <div className="text-sm text-neutral-700">{lineItem.fields.part_number?.value || '—'}</div>
                  <div className="text-sm text-neutral-700">{lineItem.fields.detailed_merchandise_description?.value || 'No description yet'}</div>
                  <div className="text-sm text-neutral-700">{lineItem.fields.quantity?.value || '—'}</div>
                  <div className="text-sm text-neutral-700">{lineItem.fields.extended_line_value?.value || '—'}</div>
                  <div>
                    <StatusPill label={labelForSectionStatus(lineItem.status)} tone={toneForSectionStatus(lineItem.status)} />
                  </div>
                </div>

                {isOpen && (
                  <div className="border-t border-neutral-200 bg-neutral-50 px-4 py-4">
                    <div className="mb-4 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => onMoveLineItem(record.id, lineItem.id, 'up')}
                        disabled={index === 0}
                        className="inline-flex items-center gap-1 rounded border border-neutral-300 px-2.5 py-1 text-xs font-semibold text-primary hover:border-secondary hover:bg-white disabled:opacity-50"
                      >
                        <ChevronUp size={12} aria-hidden="true" />
                        Move up
                      </button>
                      <button
                        type="button"
                        onClick={() => onMoveLineItem(record.id, lineItem.id, 'down')}
                        disabled={index === lineItems.length - 1}
                        className="inline-flex items-center gap-1 rounded border border-neutral-300 px-2.5 py-1 text-xs font-semibold text-primary hover:border-secondary hover:bg-white disabled:opacity-50"
                      >
                        <ChevronDown size={12} aria-hidden="true" />
                        Move down
                      </button>
                      <button
                        type="button"
                        onClick={() => onDuplicateLineItem(record.id, lineItem.id)}
                        className="inline-flex items-center gap-1 rounded border border-neutral-300 px-2.5 py-1 text-xs font-semibold text-primary hover:border-secondary hover:bg-white"
                      >
                        <Copy size={12} aria-hidden="true" />
                        Duplicate
                      </button>
                      <button
                        type="button"
                        onClick={() => onRemoveLineItem(record.id, lineItem.id)}
                        disabled={lineItems.length === 1}
                        className="inline-flex items-center gap-1 rounded border border-error px-2.5 py-1 text-xs font-semibold text-error hover:bg-white disabled:opacity-50"
                      >
                        <Trash2 size={12} aria-hidden="true" />
                        Delete
                      </button>
                    </div>

                    <div className="space-y-4">
                      {INVOICE_LINE_GROUPS.map((group) => (
                        <div key={group.id} className="rounded-lg border border-neutral-200 bg-white p-4">
                          <div className="mb-3 flex items-center gap-2">
                            <Package size={14} aria-hidden="true" className="text-secondary" />
                            <h5 className="text-sm font-semibold text-primary">{group.title}</h5>
                          </div>
                          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                            {COMMERCIAL_INVOICE_LINE_ITEM_FIELDS.filter((field) => group.fields.includes(field.key)).map((field) => {
                              const fieldState = lineItem.fields[field.key]
                              const reviewLabel = labelForFieldReviewStatus(fieldState?.reviewStatus)
                              const issues = issueMap[`${field.key}:${lineItem.id}`] ?? []
                              const isFocused = focusTarget?.documentId === record.id && focusTarget.fieldKey === field.key
                              const isAgentConfirmed = fieldState?.reviewStatus === 'agent_confirmed'

                              return (
                                <div
                                  key={field.key}
                                  className={`rounded-lg border p-4 ${field.type === 'textarea' ? 'md:col-span-2 xl:col-span-3' : ''} ${isFocused ? 'border-accent-sunset bg-accent-sunset/5' : 'border-neutral-200 bg-white'}`}
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-2">
                                        <div className="text-sm font-semibold text-primary">{field.label}</div>
                                        {field.helpText && <InlineHelp text={field.helpText} title={`${field.label} help`} align="left" />}
                                      </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      {reviewLabel && fieldState?.reviewStatus !== 'agent_confirmed' && (
                                        <StatusPill label={reviewLabel} tone="info" />
                                      )}
                                    </div>
                                  </div>
                                  <FieldControl
                                    fieldType={field.type}
                                    value={fieldState?.value ?? ''}
                                    options={field.options}
                                    placeholder={field.placeholder}
                                    onChange={(value) => onLineItemFieldChange(record.id, lineItem.id, field.key, value)}
                                    onBlur={() => void onAutoSave(record.id, { auto: true })}
                                  />
                                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-neutral-500">
                                    <span>Origin: {(fieldState?.origin ?? 'manual').replace(/_/g, ' ')}</span>
                                    {fieldState?.sourceValue && <span>Extracted: {fieldState.sourceValue}</span>}
                                    {fieldState?.sourceConfidence != null && <span>Confidence: {(fieldState.sourceConfidence * 100).toFixed(0)}%</span>}
                                  </div>
                                  {issues.length > 0 && (
                                    <ul className="mt-2 space-y-1 text-xs text-error">
                                      {issues.map((message) => (
                                        <li key={message}>{message}</li>
                                      ))}
                                    </ul>
                                  )}
                                  <div className="mt-3 flex items-center justify-between gap-3">
                                    <button
                                      type="button"
                                      onClick={() => onConfirmLineItemField(record.id, lineItem.id, field.key)}
                                      disabled={isAgentConfirmed}
                                      className={`rounded px-3 py-1.5 text-xs font-semibold ${
                                        isAgentConfirmed
                                          ? 'bg-green-100 text-green-800'
                                          : 'border border-secondary text-secondary hover:bg-secondary-lightest'
                                      }`}
                                    >
                                      {isAgentConfirmed ? 'Agent Confirmed' : 'Mark confirmed'}
                                    </button>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      ))}
                    </div>

                    <textarea
                      rows={2}
                      value={lineItem.note ?? ''}
                      onChange={(event) => onLineItemNoteChange(record.id, lineItem.id, event.target.value)}
                      onBlur={() => void onAutoSave(record.id, { auto: true })}
                      placeholder="Optional merchandise-line review note"
                      className="mt-4 w-full rounded border border-neutral-300 px-3 py-2 text-sm text-neutral-900"
                    />

                    {rowIssues.length > 0 && (
                      <div className="mt-3 rounded border border-warning/30 bg-warning/10 p-3 text-sm text-neutral-700">
                        {rowIssues.join(' ')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function InvoiceTotalsCard({
  record,
  calculatedTotals,
  onSectionNoteChange,
  onAutoSave,
}: {
  record: StructuredDocumentRecord
  calculatedTotals: ReturnType<typeof calculateInvoiceTotals>
  onSectionNoteChange: (recordId: string, sectionId: string, note: string) => void
  onAutoSave: (recordId: string, options?: { auto?: boolean }) => Promise<void>
}) {
  const sectionStates = record.sectionStates ?? {}
  const invoiceDifference = calculatedTotals.declaredInvoiceValue - calculatedTotals.calculatedLineValue
  const chargeDifference = calculatedTotals.declaredTotalCharges - calculatedTotals.itemizedCharges

  return (
    <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
      <div className="text-sm font-semibold text-primary">Valuation reconciliation</div>
      <div className="mt-3 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <SummaryValue label="Calculated line value" value={calculatedTotals.calculatedLineValue.toFixed(2)} />
        <SummaryValue label="Declared invoice value" value={calculatedTotals.declaredInvoiceValue.toFixed(2)} />
        <SummaryValue label="Calculated itemized charges" value={calculatedTotals.itemizedCharges.toFixed(2)} />
        <SummaryValue label="Declared total charges" value={calculatedTotals.declaredTotalCharges.toFixed(2)} />
      </div>
      {(Math.abs(invoiceDifference) > 0.01 || Math.abs(chargeDifference) > 0.01) && (
        <div className="mt-4 rounded border border-warning/30 bg-warning/10 p-3 text-sm text-neutral-700">
          There are unresolved invoice arithmetic mismatches. Review the merchandise lines, total invoice value, and itemized charges before acceptance or transmit.
        </div>
      )}
      <textarea
        rows={2}
        value={sectionStates.valuation_charges?.note ?? ''}
        onChange={(event) => onSectionNoteChange(record.id, 'valuation_charges', event.target.value)}
        onBlur={() => void onAutoSave(record.id, { auto: true })}
        placeholder="Optional valuation review note or explanation for a manual override"
        className="mt-4 w-full rounded border border-neutral-300 px-3 py-2 text-sm text-neutral-900"
      />
    </div>
  )
}
